import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not logged in, object = logged in
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return;

      try {
        if (firebaseUser) {
          // Get custom claims to check for admin role
          const idTokenResult = await firebaseUser.getIdTokenResult();
          
          // Force token refresh to get latest claims (useful after setting admin claims)
          if (idTokenResult.claims.admin === undefined) {
            await firebaseUser.getIdToken(true);
            const refreshedToken = await firebaseUser.getIdTokenResult();
            setUser({ ...firebaseUser, claims: refreshedToken.claims });
          } else {
            setUser({ ...firebaseUser, claims: idTokenResult.claims });
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setUser(null);
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    });

    // Cleanup function
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  // Check admin status using ONLY custom claims (secure method)
  const isAdmin = user && user.claims?.admin === true;

  return {
    user,
    loading: loading || !isInitialized,
    logout,
    isAuthenticated: !!user,
    isAdmin
  };
}; 